import { useMemo } from 'react'
import { QrcodeOutlined, UserOutlined, EditFilled, DeleteOutlined, PrinterOutlined, CheckCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Attendee, RegistrationField, AttendeeStatus } from '../types'
import { formatFieldValue } from '../utils/formatters'
import { columnWidth } from '@/config/design-tokens'
import { TableActions, AttendeeStatusBadge } from '@/components/ui'
import type { TableAction } from '@/components/ui'

/**
 * Build the public QR page link (`/r/{qr}`) for a given QR code value.
 * Exported so parents can build the same URL when rendering the QR preview modal.
 */
export const buildQrLink = (qr: string): string => {
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) ||
    (process.env.NODE_ENV === 'production'
      ? 'https://avaeventhub.com'
      : 'http://localhost:3000')

  return `${origin}/r/${qr}`
}

interface UseAttendeeColumnsProps {
  registrationFields: RegistrationField[]
  onViewProfile: (id: number) => void
  onEditAttendee: (id: number) => void
  onDeleteAttendee: (id: number, name: string) => void
  onShowQr?: (record: Attendee) => void
  onPrintBadge?: (attendee: Attendee) => void
  isPrintEnabled?: boolean
  printDisabledReason?: string
  printingAttendeeIds?: Set<number>
  onMarkAttendance?: (attendee: Attendee) => void
  /** Event-wide reason marking is unavailable, e.g. no scan points yet. */
  markAttendanceDisabledReason?: string
  showDeleteAction?: boolean
}

export const useAttendeeColumns = ({
  registrationFields,
  onViewProfile,
  onEditAttendee,
  onDeleteAttendee,
  onShowQr,
  onPrintBadge,
  isPrintEnabled = true,
  printDisabledReason,
  printingAttendeeIds,
  onMarkAttendance,
  markAttendanceDisabledReason,
  showDeleteAction = true,
}: UseAttendeeColumnsProps): ColumnsType<Attendee> => {
  return useMemo(() => {
    const dynamicCols: ColumnsType<Attendee> = []

    // Helper function to get field value from attendee
    const getFieldValue = (attendee: Attendee, field: RegistrationField) => {
      return attendee.custom_fields?.[field.identifier]
    }

    // Helper function to create column config
    const createColumnConfig = (field: RegistrationField) => {
      // Only fix the full_name column to the left
      const fixed: 'left' | 'right' | undefined = field.identifier === 'full_name' ? 'left' : undefined

      // For fixed columns, we need a width; otherwise columns are dynamic
      const width = fixed ? columnWidth.lg : undefined

      return {
        title: field.label,
        dataIndex: field.identifier,
        key: field.identifier,
        width,
        fixed,
        ellipsis: false,
        sorter: (a: Attendee, b: Attendee) => {
          const aVal = getFieldValue(a, field) || ''
          const bVal = getFieldValue(b, field) || ''
          return String(aVal).localeCompare(String(bVal))
        },
        render: (_: unknown, record: Attendee) => {
          const value = getFieldValue(record, field)

          // Special handling for full_name - make it clickable
          if (field.identifier === 'full_name' || field.identifier === 'name') {
            const displayValue = formatFieldValue(field, value)
            return (
              <a onClick={() => onViewProfile(record.id)} className="text-ava-blue-05 hover:text-ava-blue-06 cursor-pointer block truncate">
                {displayValue}
              </a>
            )
          }

          // Special handling for file_upload - show download link
          if (field.kind === 'file_upload') {
            const upload = record.file_uploads?.find(
              (f) => f.field_identifier === field.identifier,
            )
            if (!upload) return <span className="text-gray-400">-</span>
            return (
              <a href={upload.url} target="_blank" rel="noopener noreferrer" className="text-ava-blue-05 hover:text-ava-blue-06 truncate block">
                {upload.filename}
              </a>
            )
          }

          const displayValue = formatFieldValue(field, value)
          return <span className="block truncate">{displayValue}</span>
        },
      }
    }

    // Build columns in incoming registration_fields order; priority fields will still be present in that order if provided upstream
    registrationFields.forEach((field) => {
      dynamicCols.push(createColumnConfig(field))
    })

    // Add status column
    dynamicCols.push({
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: columnWidth.sm,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status: AttendeeStatus) => <AttendeeStatusBadge status={status} />,
    })

    dynamicCols.push({
      title: 'Badge Job',
      key: 'badge_print_status',
      width: columnWidth.sm,
      sorter: (a, b) => (a.badge_print_count || 0) - (b.badge_print_count || 0),
      render: (_: unknown, record: Attendee) => {
        const latestStatus = record.badge_print_logs?.[0]?.status?.toLowerCase()
        const count = record.badge_print_count || record.badge_print_logs?.length || 0
        if (latestStatus && ['pending', 'queued', 'claimed', 'printing', 'processing'].includes(latestStatus)) {
          return <span className="text-amber-700 font-medium text-xs">Pending</span>
        }
        if (latestStatus === 'failed') {
          return <span className="text-red-600 font-medium text-xs">Failed</span>
        }
        if (record.has_printed_badge || count > 0) {
          return (
            <span className="text-emerald-700 font-medium text-xs">
              Requested ({count || 1})
            </span>
          )
        }
        return <span className="text-gray-500 text-xs">Not Queued</span>
      },
    })

    // Add created_at column (displayed as "Registration Date")
    dynamicCols.push({
      title: 'Registration Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: columnWidth.sm,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => {
        if (!date) return '-'
        const formatted = new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        return <span>{formatted}</span>
      },
    })

    // Add Actions column (always visible)
    dynamicCols.push({
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: columnWidth.md,
      fixed: 'right',
      render: (_: any, record: Attendee) => {
        const markAttendanceReason =
          markAttendanceDisabledReason ||
          (record.status !== 'approved' ? 'Only approved attendees can be marked' : undefined)

        const actions: TableAction[] = [
          {
            key: 'qr',
            icon: <QrcodeOutlined />,
            onClick: () => onShowQr?.(record),
            ariaLabel: record.qr_code ? 'View QR Code' : 'QR code not assigned',
            disabled: !record.qr_code,
          },
          {
            key: 'profile',
            icon: <UserOutlined />,
            onClick: () => onViewProfile(record.id),
            ariaLabel: 'View Profile',
          },
          ...(onPrintBadge
            ? [{
                key: 'print',
                icon: <PrinterOutlined />,
                onClick: () => onPrintBadge(record),
                ariaLabel: 'Queue Badge Print',
                title: printingAttendeeIds?.has(record.id)
                  ? 'Queueing print job...'
                  : (isPrintEnabled ? 'Queue Badge Print' : (printDisabledReason || 'Print unavailable')),
                disabled: !isPrintEnabled || Boolean(printingAttendeeIds?.has(record.id)),
              } satisfies TableAction]
            : []),
          ...(onMarkAttendance
            ? [{
                key: 'mark-attendance',
                icon: <CheckCircleOutlined />,
                onClick: () => onMarkAttendance(record),
                ariaLabel: 'Mark Attendance',
                title: markAttendanceReason || 'Mark Attendance',
                disabled: Boolean(markAttendanceReason),
              } satisfies TableAction]
            : []),
          {
            key: 'edit',
            icon: <EditFilled />,
            onClick: () => onEditAttendee(record.id),
            ariaLabel: 'Edit',
          },
        ]

        if (showDeleteAction) {
          actions.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: () => onDeleteAttendee(record.id, record.full_name),
            ariaLabel: 'Delete',
            danger: true,
          })
        }

        return <TableActions actions={actions} />
      },
    })

    return dynamicCols
  }, [
    registrationFields,
    onViewProfile,
    onEditAttendee,
    onDeleteAttendee,
    onShowQr,
    onPrintBadge,
    isPrintEnabled,
    printDisabledReason,
    printingAttendeeIds,
    onMarkAttendance,
    markAttendanceDisabledReason,
    showDeleteAction,
  ])
}
